import { TEDDIES, type TeddyKey } from '../teddies.js'
import type { Clue } from './riddle-solve.js'
import type { RiddleCategory, Thing } from './riddle-things.js'

/**
 * How a clue is written down, and nothing else.
 *
 * The other half of the pair. `matches` decides what a clue means; this
 * decides what it says. Both are exhaustive switches over the clue kind, so a
 * new kind fails to compile in exactly the two places that have to change —
 * and neither can quietly drift, because a clue that says one thing and means
 * another comes out of the sweep as a riddle with two answers.
 *
 * Every sentence here is short, in the present of a story that has already
 * happened, and contains no other clue. Stacked negation — "nobody but the one
 * without the pear sat behind Monkey" — is unreadable at any age and is the
 * reason a clue is never built out of another clue.
 */

/** What a sentence needs that a clue does not carry: who is in the row, and what is in play. */
export interface Row {
  /** In seating order, front first. */
  readonly teddies: readonly TeddyKey[]
  readonly categories: readonly RiddleCategory[]
}

function teddyName(row: Row, seat: number): string {
  const key = row.teddies[seat]
  if (!key) throw new Error(`There is no seat ${seat} in this row`)

  return TEDDIES[key].name
}

function categoryOf(row: Row, key: string): RiddleCategory {
  const category = row.categories.find((candidate) => candidate.key === key)
  if (!category) throw new Error(`This riddle has no category ${key}`)

  return category
}

function thingOf(category: RiddleCategory, key: string): Thing {
  const thing = category.things.find((candidate) => candidate.key === key)
  if (!thing) throw new Error(`Category ${category.key} has no ${key}`)

  return thing
}

/**
 * "the one with the pear", "the one in the red hat".
 *
 * A holder is always named by what they have rather than by name, because a
 * clue that named them would have nothing left to deduce.
 */
function holder(category: RiddleCategory, thing: Thing): string {
  return `the one ${category.holding} ${thing.the}`
}

function sentence(words: string): string {
  return `${words.charAt(0).toUpperCase()}${words.slice(1)}.`
}

export function clueSentence(clue: Clue, row: Row): string {
  switch (clue.kind) {
    case 'direct': {
      const category = categoryOf(row, clue.category)
      const thing = thingOf(category, clue.thing)

      return sentence(`${teddyName(row, clue.seat)} ${category.past} ${thing.the}`)
    }

    case 'negative': {
      const category = categoryOf(row, clue.category)
      const thing = thingOf(category, clue.thing)

      return sentence(`${teddyName(row, clue.seat)} did not ${category.bare} ${thing.the}`)
    }

    case 'either': {
      const category = categoryOf(row, clue.category)
      const thing = thingOf(category, clue.thing)
      const [first, second] = clue.seats

      /*
       * "Sky or Bluey took the pear", never "Either Sky or Bluey". The plain
       * form dodges the question of whether "either" excludes both being true,
       * which is a thing logicians argue about and an eight-year-old should
       * not have to.
       *
       * It reads as exclusive, and that reading is only safe because the deal
       * is a bijection: exactly one teddy has the pear. The day two teddies may
       * hold apples, this sentence changes meaning without changing a word.
       */
      return sentence(
        `${teddyName(row, first)} or ${teddyName(row, second)} ${category.past} ${thing.the}`,
      )
    }

    case 'end': {
      const category = categoryOf(row, clue.category)
      const thing = thingOf(category, clue.thing)

      return sentence(`${holder(category, thing)} sat at the ${clue.end}`)
    }

    case 'neighbour': {
      const category = categoryOf(row, clue.category)
      const thing = thingOf(category, clue.thing)

      return sentence(`${holder(category, thing)} sat next to ${teddyName(row, clue.seat)}`)
    }

    case 'link': {
      const category = categoryOf(row, clue.category)
      const other = categoryOf(row, clue.otherCategory)

      return sentence(
        `${holder(category, thingOf(category, clue.thing))} ${other.past} ${
          thingOf(other, clue.otherThing).the
        }`,
      )
    }

    case 'side': {
      const category = categoryOf(row, clue.category)
      const thing = thingOf(category, clue.thing)
      const name = teddyName(row, clue.seat)

      // Both directions are said with "behind", by swapping which of the two
      // is the subject. "In front of" would be the natural opposite and is
      // three words longer, which is the difference between a sentence a child
      // reads and one she skips.
      return clue.behind === 'thing'
        ? sentence(`${holder(category, thing)} sat somewhere behind ${name}`)
        : sentence(`${name} sat somewhere behind ${holder(category, thing)}`)
    }
  }
}

/** What she is asked. One kind, because the answer is always a teddy. */
export interface RiddleQuestion {
  readonly category: string
  readonly thing: string
}

export function questionSentence(question: RiddleQuestion, row: Row): string {
  const category = categoryOf(row, question.category)

  return `Who ${category.asked} ${thingOf(category, question.thing).the}?`
}
